/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
import React, {useLayoutEffect, useRef} from "react";
import CustomFileCard from "../../../../../../../../components/CustomFileCard";
import NotInformation from "../../../../../../../../components/NotInformation";
import {calculateHeight, downloadActionFile, isSmWidth} from "../../../../../../../../Apis/functions";

/**
 * Minimum room a list of files needs to be read on its own.
 */
const MIN_EVIDENCE_HEIGHT = 160;

const EvidenceSection = ({t, item, evidence, fillHeight = true, title = 'supporting_documentation'}) => {
    const elementRef = useRef();

    /**
     * The list only takes its own height while the screen still has room for it.
     *
     * The list is read inside the card of the folio, which already scrolls, so
     * the rows displayed above it can leave it below the screen: fixing a height
     * there measures a negative one and the list is not displayed.
     *
     * The room left on the screen belongs to the list only when it is the last
     * thing the card displays, because a list that fills it pushes whatever is
     * registered below it out of sight. The height is written on the element
     * itself, so it is released every time the list is measured again: the
     * folio is read before the movements of the gate are known, and the room
     * taken for it then is the space left between the list and them.
     */
    useLayoutEffect(() => {
        const element = elementRef.current;

        if (element) element.style.height = '';

        if (isSmWidth() || !fillHeight) return;

        const top = element?.getBoundingClientRect()?.top ?? 0;

        if (window.innerHeight - (top + 100) < MIN_EVIDENCE_HEIGHT) return;

        return calculateHeight({elementRef: elementRef, bottom: 100});
    }, [item, fillHeight]);

    return (
        <>
            <p className='font-color-general-950 fs-14px ibl-font-w-600 px-0 my-3'>
                {t(title)}
            </p>

            <div className='overflow-y-auto px-0' ref={elementRef}>
                {/* The evidence is optional, so a folio that was registered
                    without it says so instead of leaving the section empty. */}
                {!evidence?.length &&
                    <NotInformation text={t('no_evidence_added')} card={true}/>
                }

                {evidence?.map((e) => {
                    return (
                        <div className={'mt-2'} key={e.id}>
                            <CustomFileCard
                                Icon={e.Icon}
                                title={e.name}
                                subtitle={
                                    <p className={'justify-content-start d-flex ibl-font-w-400 fs-10px'}>
                                        {e.size}
                                    </p>
                                }
                                fileValue={e.filePath}
                                buttonId={'btn_mrd_download_' + e.id}
                                onClick={() => downloadActionFile(e.filePath)}
                            />
                        </div>
                    );
                })}
            </div>
        </>
    );
};

export default EvidenceSection;
